import { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap';
import { FaListCheck, FaMagnifyingGlass } from 'react-icons/fa6';
import { useApp } from 'common/AppContext';
import { Link } from 'react-router-dom';

import { unwrap } from './helpers';

const PRIORITY_VARIANT = {
    1: 'secondary', // Low
    2: 'info', // Medium
    3: 'warning', // High
    4: 'danger' // Critical
};

export const BrowseCases = () => {

    const { api } = useApp();

    const [projects, setProjects] = useState([]);
    const [suites, setSuites] = useState([]);
    const [sections, setSections] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [cases, setCases] = useState([]);

    const [projectId, setProjectId] = useState('');
    const [suiteId, setSuiteId] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [search, setSearch] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Bootstrap dropdown data once.
    useEffect(() => {

        api.get('/api/testrail/projects').then(d => setProjects(unwrap(d, 'projects'))).catch(e => setError(e?.err || e?.message));
        api.get('/api/testrail/case-types').then(d => setCaseTypes(unwrap(d, 'case_types'))).catch(() => {});
        api.get('/api/testrail/priorities').then(d => setPriorities(unwrap(d, 'priorities'))).catch(() => {});

    }, [api]);

    // Project change → refetch suites.
    useEffect(() => {

        setSuiteId('');
        setSectionId('');
        setSections([]);
        setCases([]);

        if (!projectId) {

            setSuites([]); return;

        }

        api.get(`/api/testrail/projects/${projectId}/suites`)
            .then(d => {

                const list = unwrap(d, 'suites');
                setSuites(list);

                // Single-suite project: auto-select.
                if (list.length === 1) setSuiteId(String(list[0].id));

            })
            .catch(e => setError(e?.err || e?.message));

    }, [api, projectId]);

    // Suite change → refetch sections.
    useEffect(() => {

        setSectionId('');
        if (!projectId || !suiteId) {

            setSections([]); return;

        }

        api.get(`/api/testrail/projects/${projectId}/sections?suite_id=${suiteId}`)
            .then(d => setSections(unwrap(d, 'sections')))
            .catch(e => setError(e?.err || e?.message));

    }, [api, projectId, suiteId]);

    const loadCases = useCallback(() => {

        if (!projectId || !suiteId) {

            setCases([]); return;

        }

        setLoading(true);
        setError(null);

        const params = new URLSearchParams({ suite_id: suiteId });
        if (sectionId) params.set('section_id', sectionId);

        api.get(`/api/testrail/projects/${projectId}/cases?${params.toString()}`)
            .then(d => setCases(unwrap(d, 'cases')))
            .catch(e => setError(e?.err || e?.message))
            .finally(() => setLoading(false));

    }, [api, projectId, suiteId, sectionId]);

    // Auto-fetch when suite/section selection settles.
    useEffect(() => {

        loadCases();

    }, [loadCases]);

    const typeName = useMemo(
        () => Object.fromEntries(caseTypes.map(t => [t.id, t.name])),
        [caseTypes]
    );
    const priorityName = useMemo(
        () => Object.fromEntries(priorities.map(p => [p.id, p.name])),
        [priorities]
    );

    const filtered = useMemo(() => {

        const q = search.trim().toLowerCase();
        if (!q) return cases;
        return cases.filter(c => (c.title || '').toLowerCase().includes(q));

    }, [cases, search]);

    return (

        <Container className="py-4">

            <div className="d-flex align-items-center gap-2 mb-1">
                <FaListCheck className="text-primary" size={22} />
                <h3 className="m-0">
                    Browse Test Cases
                </h3>
            </div>
            <p className="text-muted">
                Browse, filter, and open TestRail cases without needing them attached to a run. Open any case
                to view, edit, or push it through the Feature Tester.
            </p>

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="py-2 px-3">

                    <Row className="g-2 mb-2">
                        <Col>
                            <InputGroup size="sm">
                                <InputGroup.Text>
                                    <FaMagnifyingGlass size={11} />
                                </InputGroup.Text>
                                <Form.Control
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Search case titles…"
                                    value={search}
                                />
                                {search && (
                                    <Button onClick={() => setSearch('')} type="button" variant="outline-secondary">
                                        ×
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                    </Row>

                    <Row className="g-2">

                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">
                                Project
                            </Form.Label>
                            <Form.Select onChange={e => setProjectId(e.target.value)} size="sm" value={projectId}>
                                <option value="">
                                    Select…
                                </option>
                                {projects.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">
                                Suite
                            </Form.Label>
                            <Form.Select disabled={!projectId} onChange={e => setSuiteId(e.target.value)} size="sm" value={suiteId}>
                                <option value="">
                                    {projectId ? 'Select…' : 'Pick a project first'}
                                </option>
                                {suites.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">
                                Section
                                <span className="text-muted ms-2">
                                    (optional)
                                </span>
                            </Form.Label>
                            <Form.Select disabled={!suiteId} onChange={e => setSectionId(e.target.value)} size="sm" value={sectionId}>
                                <option value="">
                                    All sections
                                </option>
                                {sections.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>

                    </Row>
                </Card.Body>
            </Card>

            {loading ? (
                <Spinner animation="border" />
            ) : !suiteId ? (
                <Alert variant="light">
                    Pick a project and suite to load cases.
                </Alert>
            ) : filtered.length === 0 ? (
                <Alert variant="light">
                    No cases
                    {' '}
                    {search ? 'match the search' : 'in this section'}
                    .
                </Alert>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        <Table hover responsive className="mb-0 align-middle" size="sm">
                            <thead>
                                <tr>
                                    <th>
                                        ID
                                    </th>
                                    <th>
                                        Title
                                    </th>
                                    <th>
                                        Type
                                    </th>
                                    <th>
                                        Priority
                                    </th>
                                    <th>
                                        Refs
                                    </th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(c => (
                                    <tr key={c.id}>
                                        <td className="text-muted small">
                                            C
                                            {c.id}
                                        </td>
                                        <td>
                                            <Link className="text-decoration-none" to={`/testrail/cases/${c.id}`}>
                                                {c.title}
                                            </Link>
                                        </td>
                                        <td className="text-muted small">
                                            {typeName[c.type_id] || `#${c.type_id || '?'}`}
                                        </td>
                                        <td>
                                            <Badge bg={PRIORITY_VARIANT[c.priority_id] || 'secondary'}>
                                                {priorityName[c.priority_id] || `P${c.priority_id || '?'}`}
                                            </Badge>
                                        </td>
                                        <td className="text-muted small">
                                            {c.refs || ''}
                                        </td>
                                        <td>
                                            <Link className="btn btn-sm btn-outline-primary" to={`/testrail/cases/${c.id}`}>
                                                Open
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {!loading && filtered.length > 0 && (
                <div className="text-muted small mt-2">
                    {filtered.length}
                    {' '}
                    of
                    {cases.length}
                    {' '}
                    case
                    {cases.length === 1 ? '' : 's'}
                    {search && ' (filtered)'}
                </div>
            )}

        </Container>

    );

};
